// Who is actually in the active room — a centered header above the message thread,
// mirroring iMessage's and WhatsApp's own contact-name bar. Two earlier shapes were tried
// and rejected first (see git history): a plain label above the scroller read as stray
// text rather than a heading even styled like a section title, and moving it into the
// plan-name row read as disconnected from the thread it was describing. A round of
// mockups settled on this one instead — scoped to the thread column, not the plan header,
// so it stays visually attached to the messages it describes.
//
// Never recites the plan's whole roster: a group thread's membership already equals
// "everyone", so "Everyone and Mor" says the whole thing without listing anyone, and a
// roster with a duplicate or a misnamed entry never gets a chance to recite its own
// mistakes here. The principals' room is the one case that can safely name somebody — see
// `format.ts`'s `namedCoPrincipal` for why only the two-person shape is honest to guess at.
import type { Person } from '../api/contract.js';
import { principalsRoomLabel } from '../format.js';
import { GroupIcon, PrivateIcon, TwoIcon } from './RoomIcons.js';

export type ThreadRosterProps = {
  people: readonly Person[];
  /** `'shared'` is the one group conversation; `'principals'` is the co-principals' room,
   * named when it safely can be; anything else is the reader's own 1:1. Same icon as the
   * matching room-switcher entry (`RoomIcons.tsx`), so the heading and the rail agree. */
  kind: 'shared' | 'principals' | 'private';
};

export function ThreadRoster({ people, kind }: ThreadRosterProps): JSX.Element {
  const text =
    kind === 'shared'
      ? 'Everyone and Mor'
      : kind === 'principals'
        ? `You, ${principalsRoomLabel(people)} and Mor`
        : 'You and Mor';
  const Icon =
    kind === 'shared' ? GroupIcon : kind === 'principals' ? TwoIcon : PrivateIcon;
  return (
    <div className="threadHead">
      <span className="threadHead__icon" aria-hidden="true">
        <Icon />
      </span>
      <p className="threadHead__label">{text}</p>
    </div>
  );
}
